import { SortOrderType } from '@repo/common';
import { useDebounce } from '@uidotdev/usehooks';
import { useSearchParams } from 'react-router-dom';

import { DEFAULT_SORT_BY } from '@/constants';

type UseUrlFilterProps = {
    defaultSortBy?: string;
    defaultSortOrder?: SortOrderType;
};

export const useUrlFilter = ({
    defaultSortBy = DEFAULT_SORT_BY,
    defaultSortOrder = SortOrderType.ASC,
}: UseUrlFilterProps = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get('search') || '';
    const sortBy = searchParams.get('sort_by') || defaultSortBy;
    const sortOrder = searchParams.get('sort_order') || defaultSortOrder;

    const debouncedSearchText = useDebounce(searchText, 50);

    const setSearchText = (text: string) => {
        if (text) {
            searchParams.set('search', text);
        } else {
            searchParams.delete('search');
        }
        setSearchParams(searchParams);
    };

    const setSortBy = (sortBy: string) => {
        if (sortBy) {
            searchParams.set('sort_by', sortBy);
        } else {
            searchParams.delete('sort_by');
        }
        setSearchParams(searchParams);
    };

    const setSortOrder = (order: SortOrderType) => {
        if (order) {
            searchParams.set('sort_order', order);
        } else {
            searchParams.delete('sort_order');
        }
        setSearchParams(searchParams);
    };

    const getSearchFilter = (key: string) => {
        return searchParams.get(key);
    };

    const setSearchFilter = (key: string, value?: any) => {
        if (value) {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
        setSearchParams(searchParams);
    };

    const removeSearchFilter = (key: string) => {
        searchParams.delete(key);
        setSearchParams(searchParams);
    };

    const clearSearchFilters = () => {
        searchParams?.forEach((_, key) => {
            searchParams.delete(key);
        });
        setSearchParams(searchParams);
    };

    return {
        getSearchFilter,
        setSearchFilter,
        removeSearchFilter,
        clearSearchFilters,
        setSearchText,
        searchText,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        debouncedSearchText,
    };
};
